import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import TimeSelector from '@/components/TimeSelector';

/**
 * ── /components/time-selector ────────────────────────────────────────────
 *
 * TimeSelector — the pill-shaped time-slot chip from the event page's
 * date/time picker (`DateTimePicker` in `app/events/[id]/index.tsx`, one
 * chip per bookable slot of the selected date, rendered inside a
 * `Carousel`). It's `DateSelector`'s sibling in that same picker: same
 * selected-state flip and the same `hasApTickets` AP-chip corner case, just
 * pill-shaped around a single label instead of a fixed day cell — kept as
 * its own molecule rather than sharing an atom with DateSelector since RN
 * text doesn't cascade and the two differ in shape + content (see the
 * component's own docstring).
 *
 * Takes an already-formatted `label` string (real call site builds it as
 * `${formatTimeChip(startAt)} – ${formatTimeChip(endAt)}`, a time RANGE
 * with no shared `formatEventDateTime()` output to reuse) — so unlike
 * DateSelector this preview's fixtures are plain label strings, not raw
 * ISO datetimes.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Plain label strings — TimeSelector takes an already-formatted label, it
// does no date parsing of its own (contrast DateSelector, which takes raw
// ISO and formats internally). t2 carries hasApTickets for the corner case.
const PREVIEW_TIMES = [
  { key: 't1', label: '10:30 AM – 11:30 AM', hasApTickets: false },
  { key: 't2', label: '1:00 PM – 2:00 PM', hasApTickets: true },
  { key: 't3', label: '6:30 PM – 7:30 PM', hasApTickets: false },
];

export default function TimeSelectorPreview() {
  // Drives the live demo below — mirrors DateTimePicker's `selectedSlot`,
  // one selected chip at a time among a row of them.
  const [timeKey, setTimeKey] = useState('t1');

  return (
    <>
      <Head>
        <title>TimeSelector · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Live — tap any chip to select it, same row the event page's time
            picker renders (minus Carousel's edge-to-edge bleed, which is
            Carousel's own concern, not this component's). Only one chip is
            selected at a time; t2 carries the AP chip throughout so its
            corner join is visible whichever chip is currently selected. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap a chip to select it (events/[id]&apos;s time row)
            </Text>
            <XStack gap="$sm" flexWrap="wrap">
              {PREVIEW_TIMES.map((t) => (
                <TimeSelector
                  key={t.key}
                  label={t.label}
                  selected={t.key === timeKey}
                  onPress={() => setTimeKey(t.key)}
                  hasApTickets={t.hasApTickets}
                />
              ))}
            </XStack>
          </YStack>

          {/* Static side-by-side of both states at rest — the live demo above
            only ever shows one selected chip, so this pins unselected next to
            selected to eyeball the flip directly: fill + border color + text
            color all move together, but the hairline border width is CONSTANT
            across both (see the component's docstring) so the chip never
            resizes when it's tapped. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              unselected (transparent fill, gray border, black text) vs selected
              (black fill, black border, light text) — border width never
              changes, only its color
            </Text>
            <XStack gap="$sm">
              <TimeSelector
                label="10:30 AM – 11:30 AM"
                selected={false}
                hasApTickets={false}
              />
              <TimeSelector
                label="10:30 AM – 11:30 AM"
                selected={true}
                hasApTickets={false}
              />
            </XStack>
          </YStack>

          {/* hasApTickets — the "AP" chip (brand fill, PSText.Tags) that hangs
            off the pill's bottom-left. The pill's own borderBottomLeftRadius
            drops to 0 whenever this is true, so the chip's top-left corner
            sits flush against the pill instead of leaving a rounded gap — the
            two pieces read as one shape. Shown under BOTH fill states since
            selecting the pill doesn't touch the chip (it's always brand
            fill + white text, independent of `selected`) — same corner-join
            behavior as DateSelector's day cell, applied to a pill instead. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              hasApTickets — the AP chip flattens the pill&apos;s bottom-left
              corner so it joins flush, unselected and selected
            </Text>
            <XStack gap="$sm">
              <TimeSelector
                label="1:00 PM – 2:00 PM"
                selected={false}
                hasApTickets={true}
              />
              <TimeSelector
                label="1:00 PM – 2:00 PM"
                selected={true}
                hasApTickets={true}
              />
            </XStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
